/**
 * Block 12 PR-B Phase B3 Step 9 — local types для AnketaForm template engine.
 *
 * Эти типы — runtime/UI state shape, дополняющий Zod-валидируемые `CalculatorParams`
 * из `@pomoshnik/shared/contracts/calculator`. Params хранятся как partial во время
 * draft-заполнения (refined schema validate'ит на каждом change, ADR-0020 §Q7).
 *
 * References:
 *   - ADR-0020 §Q1 (generic template + hardcoded material list per anketa)
 *   - ADR-0020 §Q2 (useReducer local per Container — этот state живёт в `useAnketaForm`)
 *   - ADR-0020 §Q4 (material row override pattern: qty/qtyAuto/qtyOverridden + ↻)
 *   - ADR-0020 §Q5 (auto-bind services через тот же override pattern)
 *   - ADR-0019 §Q11.3 (client-generated UUID для projectId)
 */

import type { CalculatorAnketaType, CalculatorParams } from '@pomoshnik/shared';

/**
 * Reusable row shape для material/service/additional/filling/lighting/consumable секций.
 *
 * Поля:
 *   - `qtyAuto` — текущее formula-computed значение (пересчитывается при изменении params).
 *   - `qty` — отображаемое значение (`qtyAuto` или manual override).
 *   - `qtyOverridden` — `true` если row.id присутствует в state.materialOverrides /
 *     state.serviceOverrides. UI рендерит ↻ icon для reset.
 *   - `cost = qty × price` — НЕ хранится в state, derived в helpers/computeTotal (Step 14).
 *   - `deeplink` — cross-block deep link target (ADR-0020 §Q11): 'decor' → Block 2,
 *     'partner' → Block 8 (/partners), 'furniture' → Block 5 (/furniture),
 *     'handles' → Block 4 (/handles). ADR-0099 (furniture target amended ADR-0100).
 */
export interface ResultRowData {
  id: string;
  name: string;
  qty: number;
  qtyAuto: number;
  qtyOverridden: boolean;
  unit: string;
  price: number;
  cost: number;
  deeplink?: 'decor' | 'partner' | 'furniture' | 'handles';
  /**
   * Phase B4 Step 5 — opt-in human-readable breakdown «почему qty такой».
   * Currently set computeMaterialRows для Kitchen `facades` row когда активны
   * adjustments (`+3.5 м² пенал`, `+3.5 м² встроенный холодильник`,
   * `+1 м² шкаф над холодильником`). ResultRow рендерит italic muted под основной
   * row content. Other anketa types — undefined.
   */
  adjustmentsDetail?: string;
  /**
   * Hotfix-2B Phase B3 Step 5 — opt-in per-row enable toggle (currently used
   * только в customRows). Undefined → trust as enabled (backward-compat для
   * material/service rows которые не используют этот флаг). False → row
   * grey'd out + cost excluded в selectCustomRowsCost.
   * Прототип ref: `screen-2.12.2:531-533` (CB checkbox + grey label when off).
   */
  enabled?: boolean;
}

export type MaterialRow = ResultRowData;

/**
 * Service row с явным указанием источника auto-bind (per прототип 2.12.2:513-520
 * + Audit Round 2 finalized + ADR-0020 §Q5). 'cut-ldsp' → corpus row (corpusQ);
 * 'edge-binding' → edge-band material row (edgeQ — hotfix-2B Step 4 fix);
 * 'cut-hdf' → hdf row (hdfQ). Manual rows (Склейка/Газель/Грузчики) —
 * autoBindKey=undefined.
 */
export interface ServiceRow extends ResultRowData {
  autoBindKey?: 'corpusQ' | 'edgeQ' | 'hdfQ';
}

export interface FieldError {
  /** Dotted path: 'height', 'length2', 'countertopSheetLen'. Empty string для root-level errors. */
  path: string;
  message: string;
}

export interface ValidationState {
  isValid: boolean;
  errors: FieldError[];
}

/**
 * Per-anketa partial params: discriminator `type` обязателен; остальные поля optional
 * во время draft-заполнения. Refined Zod schema проверяет presence + cross-field
 * invariants при каждом изменении (ADR-0020 §Q7).
 *
 * TS distributes conditional `extends` над union → получается tagged-union, где
 * `type: K` сохраняется как literal для downstream switch exhaustiveness.
 */
type PartialOfVariant<T> = T extends { type: infer K }
  ? { type: K } & Partial<Omit<T, 'type'>>
  : never;
export type AnketaParams = PartialOfVariant<CalculatorParams>;

/**
 * Full per-instance form state. Local to `<AnketaFormContainer>` через `useReducer`
 * (ADR-0020 §Q2 — no global Zustand). Persisted to IDB через `useAnketaPersist`
 * (ADR-0020 §Q6 — 300ms debounce).
 *
 * Все record-maps keyed by `rowId` (для material/service/custom) или `id` (для
 * additional/filling/consumable, см. CalculatorAdditional enum + lighting fixed 4 rows).
 */
export interface AnketaState {
  anketaType: CalculatorAnketaType;
  /** Client-generated UUID (ADR-0019 §Q11.3). Stable for form lifetime. */
  projectId: string;
  projectName: string;
  params: AnketaParams;

  // Section Б — Materials
  materialOverrides: Record<string, number>;
  materialPrices: Record<string, number>;
  /** rowId → бренд label, для ЛДСП/МДФ корпуса и фасадов (Block 2 deep-link return). */
  brandSelections: Record<string, string>;

  // Section В — Additional
  additionalToggles: Record<string, boolean>;
  additionalQuantities: Record<string, number>;
  additionalPrices: Record<string, number>;
  /**
   * Per-additional sub-selection. Phase B4 Step 2 — на текущий момент используется
   * только для `'facade-radius'` (5 типов: пленка модерн / пленка рефленка / эмаль
   * модерн / эмаль рефленка / шпон, методика §2.1 Б.2). Future toggles с
   * sub-options могут пользоваться same map.
   *
   * Validation для «toggle on AND selection undefined» добавляется в Phase B4 Step 3
   * вместе с UI sub-picker'ом — тогда же `recomputeValidation` начнёт emit'ить
   * FieldError на path `additionalSelections.<id>`. На Step 2 — только storage shape.
   */
  additionalSelections: Record<string, string>;
  /**
   * Hotfix-2B Phase B4 Step 9 — Витрина compound (Bug 12).
   *
   * Per-additional dimensions для items с compound rendering (Витрина в алюм.
   * профиле — `id === 'aluminum-glass-vitrine'`). Регулярные additional rows
   * не используют эти maps (qty + price хватает); они проставляются только
   * VitrineCompound + читаются `encodeAuxiliaryRows` / `computeTotal` для
   * vitrine row.
   *
   * Persistence: encoded в `freeItems[]` через `encodeAuxiliaryRows`
   * (Option A — no contract change). Reload `?draftId=` → значения теряются
   * (тот же gap что customRows, см. BACKLOG B-109).
   */
  additionalLengths: Record<string, number>;
  additionalHeights: Record<string, number>;
  additionalPricesPerM2: Record<string, number>;

  // Section Г — Filling (Cabinet/Balcony only)
  fillingToggles: Record<string, boolean>;
  fillingQuantities: Record<string, number>;
  fillingPrices: Record<string, number>;

  // Section Д — Lighting (master toggle + 4 fixed rows)
  lightingEnabled: boolean;
  lightingQuantities: Record<string, number>;
  lightingPrices: Record<string, number>;

  // Section Е — Consumables (5 toggleable rows)
  consumableToggles: Record<string, boolean>;
  consumableQuantities: Record<string, number>;
  consumablePrices: Record<string, number>;

  // Section Ж — Services (6 rows: 3 auto-bind + 3 manual)
  serviceToggles: Record<string, boolean>;
  serviceOverrides: Record<string, number>;
  servicePrices: Record<string, number>;

  // Section З — free-form custom rows (PRD §10)
  customRows: MaterialRow[];

  validation: ValidationState;
  isDirty: boolean;
  /** epoch ms — момент создания проекта. Preserved across RESET; never bumped. */
  createdAt: number;
  /**
   * epoch ms — last user-driven mutation. Bumped reducer-level Date.now() кроме
   * `SET_VALIDATION` (internal) и `HYDRATE_FROM_DRAFT` (preserve hydrated value).
   */
  updatedAt: number;
}

/**
 * TypeScript exhaustiveness helper. Throws at runtime если unhandled discriminant
 * leaks past static checks (например, новый CalculatorAnketaType добавлен без
 * обновления switch). Используется в reducer + computeMaterialRows + всех switch'ах
 * по `params.type` / `anketaType` / action discriminator.
 *
 * Reference: ADR-0020 §«Pre-architecture clarifications» п.6.
 */
export function assertNever(value: never, context = 'Unreachable code'): never {
  throw new Error(`${context}: unexpected value ${JSON.stringify(value)}`);
}
